<template>
  <v-card :flat="clinic">
			<v-card-title v-if="!clinic" class="title primary--text secondary">{{test.name}}</v-card-title>
			<v-card-text>
				<v-form ref="form">
					<v-container class="pb-0">
            <v-row v-if="results && !pdfMode" no-gutters class="my-4" align="center">
              <v-col>
                <v-text-field
                  v-model="searchField"
                  prepend-inner-icon="search"
                  :placeholder="$gettext('Search Field...')"
                  single-line
                  outlined
                  solo
                  hide-details
                  dense
                ></v-text-field>
              </v-col>
              <v-col md="auto">
                <v-btn-toggle color="primary" v-model="fieldContent" class="px-1">
                  <v-btn value="filled" small class="my-1 text-overline">
                    <translate>Filled</translate>
                  </v-btn>
                  <v-btn value="empty" small class="my-1 text-overline">
                    <translate>Empty</translate>
                  </v-btn>
                </v-btn-toggle>
              </v-col>
            </v-row>
            <v-row v-if="existingVariables && !pdfMode" no-gutters>
              <v-btn-toggle color="primary" v-for="variable in existingVariables" :key="variable.id" v-model="variable.selected" class="px-1">
                <v-btn v-for="option in variable.options" :key="option.id" :value="option.id" small class="my-1 text-overline">
                  <span v-text="option.id == 1 ? $gettext('Yes') : option.id == 2 ? $gettext('No') : option.name"></span>
                </v-btn>
              </v-btn-toggle>
            </v-row>
						<v-row no-gutters>
							<v-col v-for="(field, i) in filteredFields" :key="field.id" cols="12" sm="12">
                <v-row no-gutters align="center" style="min-height: 50px">
                  <v-col cols="12">
                    <span class="text-subtitle-1 mx-1">{{i + 1}}.</span>
                    <span class="text-subtitle-1 font-weight-black">{{field.name}}</span>
                  </v-col>
                  <v-col cols="12">
                    <template v-if="field.test_variable.id == 1">
                      <div v-if="results" class="text--disabled mt-2 ml-2" style="white-space: pre-wrap;">{{field.value}}</div>
                      <v-textarea
                        v-else
                        v-model="field.value"
                        class="px-2 mt-2"
                        outlined
                        :disabled="results"
                        hide-details
                        auto-grow
                      />
                    </template>
                    <template v-else-if="field.test_variable.id == 3">
                      <v-col class="pr-4">
                        <v-slider
                          v-if="!pdfMode"
                          v-model="field.value"
                          class="align-center"
                          :max="field.degree_max"
                          :min="1"
                          hide-details
                          :disabled="results"
                          :ticks="true"
                          tick-size="4"
                        >
                          <template v-slot:append>
                            <v-text-field
                              v-model="field.value"
                              class="mt-0 pt-0"
                              hide-details
                              single-line
                              type="number"
                              style="width: 60px"
                            ></v-text-field>
                          </template>
                        </v-slider>
                        <v-text-field
                          v-else
                          outlined
                          :label="`(1 - ${field.degree_max})`"
                          style="max-width: 100px"
                          persistent-placeholder
                        />
                      </v-col>
                    </template>
                    <template v-else>
                      <v-radio-group
                        v-model="field.value"
                        row
                        :disabled="results"
                      >
                        <v-radio
                          v-for="option in field.test_variable.options"
                          :key="option.id"
                          :label="option.id == 1 ? $gettext('Yes') : option.id == 2 ? $gettext('No') : option.name"
                          :value="option.id"
                        ></v-radio>
                      </v-radio-group>
                    </template>
                  </v-col>
                </v-row>
							</v-col>
						</v-row>
					</v-container>
				</v-form>
			</v-card-text>
			<v-divider v-if="!clinic && !pdfMode"></v-divider>
			<v-card-actions v-if="!clinic && !pdfMode">
				<v-btn v-if="results" color="primary darken-1" text @click="hide">
					<translate>Close</translate>
				</v-btn>
				<v-btn v-else color="primary darken-1" text @click="hide">
					<translate>Cancel</translate>
				</v-btn>
				<v-spacer></v-spacer>
				<v-btn v-if="!edit && !results" color="primary darken-1" text @click="submitTest">
					<translate>Create</translate>
				</v-btn>
				<v-btn v-if="edit" color="primary darken-1" text @click="editTest">
					<translate>Save</translate>
				</v-btn>
        <v-btn v-if="results" color="primary darken-1" text @click="$emit('open-edit')">
					<translate>Edit</translate>
				</v-btn>
			</v-card-actions>
	</v-card>
</template>

<script>
export default {
  name: "CustomTestForm",
  props: {
		test: {},
		edit: {
			default: false
		},
		results: {
			default: false
		},
		clinic: {
			default: false
		},
    pdfMode: {
      default: false
    }
	},
  data: () => ({
    searchField: "",
    fieldContent: 'filled',
    existingVariables: []
  }),
  computed: {
    filteredFieldsBySearch() {
      if(!this.results || !this.searchField) return this.test.fields;
      let fields = [];
      for(let i=0; i < this.test.fields.length; i++) {
        if(this.test.fields[i].name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").indexOf(this.searchField.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")) > -1) {
          fields.push(this.test.fields[i]);
        }
      }
      return fields;
    },
    filteredFieldsByContent() {
      if(!this.results || !this.fieldContent) return this.filteredFieldsBySearch;
      let fields = [];
      for(let i=0; i < this.filteredFieldsBySearch.length; i++) {
        if(this.fieldContent == 'filled' && (this.filteredFieldsBySearch[i].value != null && this.filteredFieldsBySearch[i].value != '')) fields.push(this.filteredFieldsBySearch[i]);
        if(this.fieldContent == 'empty' && (this.filteredFieldsBySearch[i].value == null || this.filteredFieldsBySearch[i].value == '')) fields.push(this.filteredFieldsBySearch[i]);
      }
      return fields;
    },
    filteredFields() {
      if(!this.results || !this.isVariableFiltered) return this.filteredFieldsByContent;
      let fields = [];
      for(let i=0; i < this.filteredFieldsByContent.length; i++) {
        if(this.filteredFieldsByContent[i].value) {
          for(let h=0; h < this.existingVariables.length; h++) {
            if(this.existingVariables[h].selected && this.filteredFieldsByContent[i].value == this.existingVariables[h].selected) {
              fields.push(this.filteredFieldsByContent[i]);
              break;
            }
          }
        }
      }
      return fields;
    },
    isVariableFiltered() {
      for(let h=0; h < this.existingVariables.length; h++) {
        if(this.existingVariables[h].selected) return true;
      }
      return false;
    }
  },
  methods: {
		submitTest() {
			this.$emit("create-test", this.test);
      this.hide();
		},
		editTest() {
			this.$emit("edit-test", this.test);
			this.hide();
		},
		hide() {
			this.$emit("hide");
		}
	},
  created() {
    if(this.results) {
      for(let i=0; i < this.test.fields.length; i++) {
        if(this.test.fields[i].test_variable && this.test.fields[i].test_variable.options.length > 0) {
          let index = this.existingVariables.findIndex(v => v.id == this.test.fields[i].test_variable.id);
          if(index == -1) {
            this.$set(this.test.fields[i].test_variable, 'selected', null);
            this.existingVariables.push(this.test.fields[i].test_variable);
          }
        }
      }
    }
  }
}
</script>